import moment from 'moment';
import {
    dbDateFormat,
    dbTimeFormat,
    getBoxDateFormat,
    getBoxTimeFormat,
    getLocale,
    sortCourseEventDays,
} from '../../../../../../services/Helpers';

const getShortDateFormat = () => (getBoxDateFormat() === 'MM/DD/YYYY' ? 'MM/DD' : 'DD/MM');

export const formatSwapShortDate = (date) => moment(date, dbDateFormat).format(getShortDateFormat());

const formatSwapDateRange = (startDate, endDate) => {
    if (!startDate) {
        return '';
    }
    if (!endDate) {
        return formatSwapShortDate(startDate);
    }
    return `${formatSwapShortDate(startDate)}–${formatSwapShortDate(endDate)}`;
};

export const formatSwapDayTimeLabel = (day, locale = getLocale()) => {
    const weekday = moment().weekday(day.day_of_week).locale(locale).format('dddd');
    const timeFormat = getBoxTimeFormat();
    const start = moment(day.start, dbTimeFormat).locale(locale).format(timeFormat);
    const end = moment(day.end, dbTimeFormat).locale(locale).format(timeFormat);
    return `${weekday} ${start}–${end}`;
};

export const formatSwapDayPill = (seriesTrackRelation, day, locale = getLocale()) => {
    const series = seriesTrackRelation.series;
    const dateRange = formatSwapDateRange(series?.start_date, series?.end_date);
    const dayTime = formatSwapDayTimeLabel(day, locale);
    const seriesName = series?.series_name ?? '';
    return [dateRange, seriesName].filter(Boolean).join(' ') + (dayTime ? ` · ${dayTime}` : '');
};

export const isTrackSwapWeeklyLimitReached = (seriesTrackRelation, fromSeriesId) => {
    if (seriesTrackRelation.session_per_week == null) {
        return false;
    }
    const relationSeriesId = seriesTrackRelation.series_id ?? seriesTrackRelation.series?.id;
    // Swapping within the same series replaces a day — count stays the same.
    if (fromSeriesId != null && fromSeriesId === relationSeriesId) {
        return false;
    }
    const registeredCount = seriesTrackRelation.registered_days_count ?? 0;
    const projectedCount = fromSeriesId != null && fromSeriesId !== relationSeriesId ? registeredCount + 1 : registeredCount;
    return projectedCount > seriesTrackRelation.session_per_week;
};

export const findSwapEvent = (seriesTrackRelations, eventId) => {
    if (!eventId) {
        return null;
    }
    for (const seriesTrackRelation of seriesTrackRelations ?? []) {
        const days = [...(seriesTrackRelation.series.current_days ?? []), ...(seriesTrackRelation.series.available_days ?? [])];
        for (const day of days) {
            const id = day.id ?? day.series_event_id;
            if (id === eventId) {
                return {seriesTrackRelation, day: {...day, id}};
            }
        }
    }
    return null;
};

export const getPostSwapDays = (seriesTrackRelations, fromEventId, toEventId) => {
    const mapDay = (seriesTrackRelation, day) => ({
        ...day,
        id: day.id ?? day.series_event_id,
        series_name: seriesTrackRelation.series?.series_name,
    });
    const toContext = findSwapEvent(seriesTrackRelations, toEventId);
    const toDay = toContext ? mapDay(toContext.seriesTrackRelation, toContext.day) : null;
    const currentDays = [];
    (seriesTrackRelations ?? []).forEach((seriesTrackRelation) => {
        (seriesTrackRelation.series?.current_days ?? []).forEach((day) => {
            currentDays.push(mapDay(seriesTrackRelation, day));
        });
    });
    const sortedCurrent = sortCourseEventDays(currentDays);
    if (!toDay) {
        return sortedCurrent;
    }
    return sortCourseEventDays(
        sortedCurrent.filter(day => day.id !== fromEventId).concat(toDay),
    );
};

export const hasRegisteredSwapDays = (seriesTrackRelations) => (seriesTrackRelations ?? []).some(
    seriesTrackRelation => (seriesTrackRelation.series?.current_days?.length ?? 0) > 0,
);

export const getSwapTargetCourses = (seriesTrackRelations, {fromEventId, allowedEventIds = null}) => {
    const registeredIds = new Set();
    (seriesTrackRelations ?? []).forEach((seriesTrackRelation) => {
        (seriesTrackRelation.series?.current_days ?? []).forEach((day) => {
            registeredIds.add(day.id ?? day.series_event_id);
        });
    });

    return (seriesTrackRelations ?? [])
        .map((seriesTrackRelation) => ({
            ...seriesTrackRelation,
            series: {
                ...seriesTrackRelation.series,
                available_days: sortCourseEventDays(
                    (seriesTrackRelation.series?.available_days ?? []).filter((day) => {
                        const id = day.id ?? day.series_event_id;
                        if (id === fromEventId || registeredIds.has(id)) {
                            return false;
                        }
                        if (allowedEventIds && !allowedEventIds.includes(id)) {
                            return false;
                        }
                        return true;
                    }),
                ),
            },
        }))
        .filter(seriesTrackRelation => (seriesTrackRelation.series?.available_days?.length ?? 0) > 0);
};

export const getFlatAvailableDays = (seriesTrackRelations) => {
    const days = [];
    (seriesTrackRelations ?? []).forEach((seriesTrackRelation) => {
        (seriesTrackRelation.series?.available_days ?? []).forEach((day) => {
            days.push({...day, id: day.id ?? day.series_event_id});
        });
    });
    return sortCourseEventDays(days);
};
